import {
  useEditor,
  EditorContent,
  BubbleMenu,
  FloatingMenu,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import CodeBlock from "@tiptap/extension-code-block";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import Toolbar from "./components/Toolbar";
import BubbleMenuComponent from "./components/BubbleMenu";
import FloatingMenuComponent from "./components/FloatingMenu";
import { YouTube } from "./extensions/YouTube";

interface WysiwygEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  className?: string;
  placeholder?: string;
  readOnly?: boolean;
  ariaLabel?: string;
}

export const WysiwygEditor = ({
  value = "",
  onChange,
  error,
  className,
  placeholder = "Start writing...",
  readOnly = false,
  ariaLabel = "Rich text editor",
}: WysiwygEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: "text-primary-500 dark:text-primary-400 hover:underline",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "rounded-lg max-w-full",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "border-collapse table-auto w-full",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class:
            "border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 p-2",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-primary-200 dark:border-primary-800 p-2",
        },
      }),
      // CodeBlock.configure({
      //   HTMLAttributes: {
      //     class: 'rounded-lg bg-primary-50 dark:bg-primary-900/30 p-4 font-mono text-sm',
      //   },
      // }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      YouTube,
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-label": ariaLabel,
        "data-test-id": "wysiwyg-editor",
        class: classNames(
          "prose prose-primary dark:prose-invert max-w-none",
          "focus:outline-none",
          "min-h-[200px]",
          "p-4",
          "[&_.is-editor-empty]:before:text-primary-400",
          "[&_.is-editor-empty]:before:content-[attr(data-placeholder)]",
          "[&_.is-editor-empty]:before:float-left",
          "[&_.is-editor-empty]:before:pointer-events-none",
          "[&_p]:my-2",
          "[&_h1]:text-3xl [&_h2]:text-2xl [&_h3]:text-xl [&_h4]:text-lg",
          "[&_h1,&_h2,&_h3,&_h4]:font-medium [&_h1,&_h2,&_h3,&_h4]:text-primary-700 dark:[&_h1,&_h2,&_h3,&_h4]:text-primary-300",
          error
            ? "border-error-300 dark:border-error-700"
            : "border-primary-200 dark:border-primary-800",
          className,
        ),
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  const addImage = useCallback(() => {
    const url = window.prompt("URL");

    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="space-y-1.5 font-sans">
      <div
        className={classNames(
          "rounded-lg border bg-white dark:bg-content-900",
          "relative",
          error
            ? "border-error-300 dark:border-error-700"
            : "border-primary-200 dark:border-primary-800",
        )}
      >
        {!readOnly && <Toolbar editor={editor} onAddImage={addImage} />}

        {!readOnly && (
          <BubbleMenu editor={editor}>
            <BubbleMenuComponent editor={editor} />
          </BubbleMenu>
        )}

        {!readOnly && (
          <FloatingMenu editor={editor}>
            <FloatingMenuComponent editor={editor} />
          </FloatingMenu>
        )}

        <div className="w-full">
          <EditorContent editor={editor} />
        </div>
      </div>

      {error && (
        <p className="text-sm font-medium text-error-600 dark:text-error-400">
          {error}
        </p>
      )}
    </div>
  );
};

export default WysiwygEditor;
